import { z } from 'zod'
import { moneyAmountSchema } from '../validators/money.validator.js'
import { taxRatePercentSchema } from '../validators/tax-rate.validator.js'
import { uuidSchema } from '../validators/uuid.validator.js'
import { seasonStartMonthsSchema } from '../validators/season-start-months.validator.js'
import {
  licenseStateSchema,
  organizationNameSchema,
  organizationPhoneSchema,
  proposalValidityDaysSchema,
  seasonReminderLeadDaysSchema,
} from '../validators/organization-fields.validator.js'

/**
 * Organization-wide settings.
 *
 * One record for the whole install — the repo assumes a single organization
 * throughout — so there is no id to address it by on the wire. A caller asks
 * for "the settings", not "settings 3", and putting an id in the DTO would
 * imply a choice that does not exist.
 */
export const organizationDtoSchema = z.object({
  /**
   * The business name, as clients see it.
   *
   * On the proposal letterhead, in the subject line of mail to clients, and
   * across the app. Never empty — there is no sensible way to render a
   * proposal from nobody — so the API refuses a blank rather than storing one.
   * The same check the update accepts, including the ban on a line break.
   */
  name: organizationNameSchema,

  /**
   * Dollars per crew-hour of tree work, as an exact decimal string ("85.00"),
   * or null when nobody has set a rate yet.
   *
   * Null is a real state, not a missing value to paper over: work cannot be
   * priced until someone says what the rate is, and a default would let a quote
   * be produced from a number nobody chose.
   */
  treeLaborRatePerHour: moneyAmountSchema.nullable(),

  /**
   * Sales tax as a percent, to three decimal places ("8.250"), or null when
   * nobody has set a rate.
   *
   * Null and zero both mean a proposal carries no tax. Null is the install
   * that has never said; zero is someone saying so. Neither invents a rate.
   */
  taxRatePercent: taxRatePercentSchema.nullable(),

  /*
   * Insurance and credentials, shown on every proposal.
   *
   * Flat rather than nested under an `insurance` key: the columns are flat, the
   * update input is a flat partial patch, and a nesting level here would have
   * to be flattened again at both ends for no gain the settings form does not
   * already get by grouping them visually.
   */
  insuranceCarrier: z.string().nullable(),
  /** Coverage limits as exact decimal strings, or null when not recorded. */
  generalLiabilityPerOccurrence: moneyAmountSchema.nullable(),
  generalLiabilityAggregate: moneyAmountSchema.nullable(),
  autoLiabilityPerAccident: moneyAmountSchema.nullable(),
  carriesWorkersCompensation: z.boolean(),
  providesAdditionalInsured: z.boolean(),
  isaCertificationNumber: z.string().nullable(),
  contractorLicenseNumber: z.string().nullable(),
  /** Two letters, upper case. The same check the update accepts. */
  licenseState: licenseStateSchema.nullable(),
  /** The licensed irrigator and backflow tester, for proposals that bid irrigation. */
  irrigatorLicenseNumber: z.string().nullable(),
  backflowTesterLicenseNumber: z.string().nullable(),
  /** The number clients are told to call. Null until somebody enters it. */
  phone: organizationPhoneSchema.nullable(),

  /**
   * The logo printed on every proposal, or null when none is set.
   *
   * `logoUrl` is an ABSOLUTE, unauthenticated URL, unlike every other file in
   * this app. It has to be: a client opens their proposal with no account, and
   * a proposal whose letterhead is a broken image is worse than one with no
   * letterhead at all. A logo is published branding — it is on every document
   * that leaves the building — so there is nothing here to keep private.
   */
  /**
   * Whether a client may decline individual pruning job types.
   *
   * Only pruning work: a client can always decline a problem, a property or
   * a species, and can never decline access and clean-up.
   */
  clientsMaySelectJobTypes: z.boolean(),
  /** Whether every tree carrying pruning jobs must have its time split. */
  requireItemizedEstimates: z.boolean(),
  /**
   * Days a proposal's price holds after it is sent, or null for no expiry.
   * Applied at send to a version with no date of its own.
   */
  proposalValidityDays: proposalValidityDaysSchema.nullable(),
  /**
   * The month each season starts in, which is how a season chosen in the
   * survey becomes "now" or "later" (docs/seasonal-work.md).
   */
  seasonStartMonths: seasonStartMonthsSchema,
  /**
   * Days before a season starts that the office is reminded of work accepted
   * proposals estimated for it.
   */
  seasonReminderLeadDays: seasonReminderLeadDaysSchema,

  logoDocumentId: uuidSchema.nullable(),
  logoUrl: z.string().nullable(),

  updatedAt: z.string().datetime(),
})

export type OrganizationDto = z.infer<typeof organizationDtoSchema>

/**
 * The insurance and credentials, as stored. The proposal builder and the
 * settings form each take their own shape from this one, so neither can drift
 * from what the organization actually records.
 */
export type OrganizationInsurance = Pick<
  OrganizationDto,
  | 'insuranceCarrier'
  | 'generalLiabilityPerOccurrence'
  | 'generalLiabilityAggregate'
  | 'autoLiabilityPerAccident'
  | 'carriesWorkersCompensation'
  | 'providesAdditionalInsured'
  | 'isaCertificationNumber'
  | 'contractorLicenseNumber'
  | 'licenseState'
  | 'irrigatorLicenseNumber'
  | 'backflowTesterLicenseNumber'
>

/**
 * The parts of the settings anyone may see, with no account.
 *
 * Two fields, both of which are published branding by definition: they are
 * printed on every proposal that leaves the building. The login screen, the
 * invite-accept screen and a client reading a shared proposal all need them
 * and none of the three has a session, so a deliberately tiny public shape is
 * better than making any of them go without — or, worse, making more of the
 * settings readable than needs to be.
 */
export const organizationBrandingDtoSchema = z.object({
  name: organizationNameSchema,
  logoUrl: z.string().nullable(),
})

export type OrganizationBrandingDto = z.infer<
  typeof organizationBrandingDtoSchema
>
